/*
 * Copyright (c) 2026 Paxia AI Pty Ltd. All rights reserved.
 * This software is proprietary and confidential.
 * No part of this codebase may be copied, modified, published, distributed, sublicensed, or used without prior written permission from Paxia AI Pty Ltd.
 */

import React, { createContext, useContext, useState, useRef, useCallback, useEffect } from 'react';
import { fetchAccountDetails, fetchOpenTrades } from '../api/api';
import API_BASE_URL, { WS_BASE_URL } from '../api/config';

const AccountDataContext = createContext();

export const useAccountData = () => useContext(AccountDataContext);

/**
 * Normalize a position object so it always carries the field names that
 * OpenPositions.jsx (and other UI components) expect — i.e. the same
 * shape the WebSocket `account_update` messages use.
 *
 * The HTTP snapshot endpoints (`/accounts/details/`, `/trades/open-positions/`)
 * return slightly different key names (e.g. `open_price` vs `price_open`,
 * `stop_loss` vs `sl`, `take_profit` / `profit_target` vs `tp`, `instrument`
 * vs `symbol`, `lot_size` vs `volume`, `entry_price` vs `price_open`).
 *
 * We keep every original key intact and **add** the canonical aliases so the
 * UI never sees undefined for a field that was present under a different name.
 */
const normalizePosition = (pos) => {
    if (!pos || typeof pos !== 'object') return pos;
    const p = { ...pos };

    // ticket / position_id
    if (p.ticket == null && p.position_id != null) p.ticket = p.position_id;

    // symbol / instrument
    if (p.symbol == null && p.instrument != null) p.symbol = p.instrument;

    // volume / lot_size
    if (p.volume == null && p.lot_size != null) p.volume = p.lot_size;

    // price_open / open_price / entry_price
    if (p.price_open == null) {
        p.price_open = p.open_price ?? p.entry_price ?? null;
    }

    // price_current / current_price
    if (p.price_current == null && p.current_price != null) p.price_current = p.current_price;

    // sl / stop_loss
    if (p.sl == null && p.stop_loss != null) p.sl = p.stop_loss;

    // tp / take_profit / profit_target
    if (p.tp == null) {
        p.tp = p.take_profit ?? p.profit_target ?? null;
    }

    // Ensure direction is uppercase ("BUY" / "SELL") for consistent UI styling
    if (typeof p.direction === 'string') {
        p.direction = p.direction.toUpperCase();
    }

    return p;
};

const normalizePositions = (list) => {
    if (!Array.isArray(list)) return list;
    return list.map(normalizePosition);
};

export const AccountDataProvider = ({ children }) => {
    const [balance, setBalance] = useState(null);
    const [equity, setEquity] = useState(null);
    const [freeMargin, setFreeMargin] = useState(null);
    const [cashAvailable, setCashAvailable] = useState(null);
    const [floatingPnl, setFloatingPnl] = useState(null);
    const [currency, setCurrency] = useState(null);
    const [openPositions, setOpenPositions] = useState([]);
    const [pendingOrders, setPendingOrders] = useState([]);
    const [isConnected, setIsConnected] = useState(false);
    const [connectedAccountId, setConnectedAccountId] = useState(null);
    const wsRef = useRef(null);
    const connectReqRef = useRef(0); // used to ignore stale HTTP responses
    const connectedAccountRef = useRef(null); // track which account is currently connected

    const connect = useCallback((accountId) => {
        // If already connected to the same account with an open socket, skip reconnection
        if (
            connectedAccountRef.current === accountId &&
            wsRef.current &&
            (wsRef.current.readyState === WebSocket.OPEN || wsRef.current.readyState === WebSocket.CONNECTING)
        ) {
            return;
        }

        const previousSocket = wsRef.current;
        wsRef.current = null;
        if (previousSocket) {
            try { previousSocket.close(); } catch { /* ignore */ }
        }
        connectedAccountRef.current = accountId;
        setConnectedAccountId(accountId);
        // Increment request id to invalidate in-flight snapshot fetches from previous accounts
        connectReqRef.current += 1;
        const reqId = connectReqRef.current;

        // Reset states to show loading/empty data immediately on account switch
        setBalance(null);
        setEquity(null);
        setFreeMargin(null);
        setCashAvailable(null);
        setFloatingPnl(null);
        setCurrency(null);
        setOpenPositions([]);
        setPendingOrders([]);
        setIsConnected(false); // Also reset connection status

        // Fire an HTTP snapshot to prime UI quickly while WS connects
        (async () => {
            try {
                const [detailsRes, openTradesRes] = await Promise.allSettled([
                    fetchAccountDetails(accountId),
                    fetchOpenTrades(accountId),
                ]);
                // Ignore if a newer connect was initiated
                if (connectReqRef.current !== reqId) return;

                if (detailsRes.status === 'fulfilled' && detailsRes.value) {
                    const d = detailsRes.value;
                    if (typeof d.balance !== 'undefined') {
                        const b = Number(d.balance);
                        if (!isNaN(b)) setBalance(b);
                    }
                    if (typeof d.equity !== 'undefined') {
                        const e = Number(d.equity);
                        if (!isNaN(e)) setEquity(e);
                    }
                    if (typeof d.free_margin !== 'undefined') {
                        const fm = Number(d.free_margin);
                        if (!isNaN(fm)) setFreeMargin(fm);
                    }
                    if (typeof d.cash_available !== 'undefined') {
                        const cash = Number(d.cash_available);
                        if (!isNaN(cash)) setCashAvailable(cash);
                    }
                    if (typeof d.floating_pnl !== 'undefined') {
                        const pnl = Number(d.floating_pnl);
                        if (!isNaN(pnl)) setFloatingPnl(pnl);
                    }
                    if (d.currency) setCurrency(d.currency);
                    if (Array.isArray(d.open_positions)) setOpenPositions(normalizePositions(d.open_positions));
                    if (Array.isArray(d.pending_orders)) setPendingOrders(d.pending_orders);
                }

                if (openTradesRes.status === 'fulfilled') {
                    const v = openTradesRes.value;
                    // The endpoint returns { open_positions: [...] } or { results: [...] } or a plain array
                    const list = Array.isArray(v)
                        ? v
                        : Array.isArray(v?.open_positions)
                            ? v.open_positions
                            : Array.isArray(v?.results)
                                ? v.results
                                : null;
                    if (Array.isArray(list)) {
                        const normalized = normalizePositions(list);
                        // Only set if not already populated by details to avoid flicker
                        setOpenPositions(prev => (prev && prev.length ? prev : normalized));
                    }
                }
            } catch (e) {
                console.error('Failed to fetch initial snapshot:', e);
            }
        })();

        const authToken = localStorage.getItem("token");
        if (!authToken) {
            console.error("Authentication token not found for WebSocket.");
            setIsConnected(false);
            return;
        }

        const origin = WS_BASE_URL || (() => {
            const proto = window.location.protocol === 'https:' ? 'wss' : 'ws';
            return `${proto}://${window.location.host}`;
        })();

        const ws = new WebSocket(`${origin}/ws/accounts/${accountId}/?token=${authToken}`);
        wsRef.current = ws;

        ws.onopen = () => {
            if (wsRef.current !== ws || connectedAccountRef.current !== accountId) return;
            console.log("[open] Account data connection established");
            setIsConnected(true);
        };

        ws.onclose = (event) => {
            if (wsRef.current !== ws || connectedAccountRef.current !== accountId) return;
            if (event.wasClean) {
                console.log(`[close] Account data connection closed cleanly, code=${event.code} reason=${event.reason}`);
            } else {
                console.log('[close] Account data connection died');
            }
            setIsConnected(false);
        };

        ws.onerror = (error) => {
            if (wsRef.current !== ws || connectedAccountRef.current !== accountId) return;
            console.error(`[error] Account data websocket error: ${error.message}`);
            setIsConnected(false);
        };

        ws.onmessage = (event) => {
            if (wsRef.current !== ws || connectedAccountRef.current !== accountId) return;
            //console.log("[message] Account data received:", event.data);
            const message = JSON.parse(event.data);

            if (message.error) {
                console.error(`[error] Server error: ${message.error}`);
                return;
            }

            if (message.type === 'account_update') {
                const { balance, equity, free_margin, cash_available, floating_pnl, currency, open_positions, pending_orders } = message.data;
                if (typeof balance === 'number') setBalance(balance);
                if (typeof equity === 'number') setEquity(equity);
                if (typeof free_margin === 'number') setFreeMargin(free_margin);
                else if (free_margin === null || free_margin === undefined) setFreeMargin(null);
                if (typeof cash_available === 'number') setCashAvailable(cash_available);
                else if (cash_available === null || cash_available === undefined) setCashAvailable(null);
                if (typeof floating_pnl === 'number') setFloatingPnl(floating_pnl);
                else if (floating_pnl === null || floating_pnl === undefined) setFloatingPnl(null);
                if (currency) setCurrency(currency);
                
                setOpenPositions(prevOpenPositions => {
                    const newOpenPositions = normalizePositions(open_positions || []);
                    if (JSON.stringify(prevOpenPositions) !== JSON.stringify(newOpenPositions)) {
                        return newOpenPositions;
                    }
                    return prevOpenPositions;
                });

                setPendingOrders(prevPendingOrders => {
                    const newPendingOrders = pending_orders || [];
                    if (JSON.stringify(prevPendingOrders) !== JSON.stringify(newPendingOrders)) {
                        return newPendingOrders;
                    }
                    return prevPendingOrders;
                });
            }
        };
    }, []);

    const disconnect = useCallback(() => {
        // Invalidate any in-flight snapshot
        connectReqRef.current += 1;
        connectedAccountRef.current = null;
        setConnectedAccountId(null);
        if (wsRef.current) {
            try {
                if (wsRef.current.readyState === WebSocket.OPEN) {
                    wsRef.current.send(JSON.stringify({ action: 'unsubscribe' }));
                }
                wsRef.current.close();
            } catch { /* ignore */ }
            wsRef.current = null;
        }
        setIsConnected(false);
        console.log("[disconnect] Account data connection closed.");
    }, []);

    // Close WebSocket when user leaves the website, logs out, or provider unmounts
    useEffect(() => {
        const closeSocket = () => {
            if (wsRef.current) {
                try {
                    if (wsRef.current.readyState === WebSocket.OPEN) {
                        wsRef.current.send(JSON.stringify({ action: 'unsubscribe' }));
                    }
                } catch { /* ignore */ }
                try { wsRef.current.close(); } catch { /* ignore */ }
                wsRef.current = null;
            }
            connectedAccountRef.current = null;
        };
        window.addEventListener('beforeunload', closeSocket);
        // On logout, close socket AND clear stale account data from React state
        const resetState = () => {
            closeSocket();
            setBalance(null);
            setEquity(null);
            setFreeMargin(null);
            setCashAvailable(null);
            setFloatingPnl(null);
            setCurrency(null);
            setOpenPositions([]);
            setPendingOrders([]);
            setIsConnected(false);
        };
        window.addEventListener('paxia:logout', resetState);
        return () => {
            window.removeEventListener('beforeunload', closeSocket);
            window.removeEventListener('paxia:logout', resetState);
            // Provider unmounting (full app teardown) — clean up
            closeSocket();
        };
    }, []);

    const value = {
        connect,
        disconnect,
        isConnected,
        balance,
        equity,
        freeMargin,
        cashAvailable,
        floatingPnl,
        currency,
        openPositions,
        pendingOrders,
        connectedAccountId,
    };

    return (
        <AccountDataContext.Provider value={value}>
            {children}
        </AccountDataContext.Provider>
    );
};
